import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    createTag,
    deleteTag,
    fetchAllTags,
    searchTagsApi,
    updateTag,
    fetchContentsForTagName,
    TagContentItem,
} from '../../../../api/tags';
import '../../../../assets/styles/tags-manager.scss';

type TagRow = { id: number; name: string; content_count?: number };

export default function TagsManager() {
    const [items, setItems] = useState<TagRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // sterowanie
    const [search, setSearch] = useState('');
    const [newTag, setNewTag] = useState('');

    // akordeon (może być wiele otwartych – jak w categories)
    const [openSet, setOpenSet] = useState<Set<number>>(new Set());

    // treści per tag
    const [contentsByTag, setContentsByTag] = useState<Record<number, TagContentItem[]>>({});
    const [loadingByTag, setLoadingByTag] = useState<Record<number, boolean>>({});

    // edycja inline
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');
    const [savingId, setSavingId] = useState<number | null>(null);
    const editInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await fetchAllTags();
            setItems(list);
            // utrzymaj otwarte wiersze, jeśli jeszcze istnieją
            setOpenSet(prev => {
                const keep = new Set<number>();
                const ids = new Set(list.map(t => t.id));
                prev.forEach(id => { if (ids.has(id)) keep.add(id); });
                return keep;
            });
            // jeśli edytowałeś i tag zniknął — wyczyść tryb edycji
            if (editingId && !list.find(t => t.id === editingId)) {
                setEditingId(null);
                setEditValue('');
            }
        } catch (e: any) {
            setError(e?.error || 'Nie udało się pobrać tagów');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        if (!s) return items;
        return items.filter(t => t.name.toLowerCase().includes(s) || String(t.id).includes(s));
    }, [items, search]);

    // tworzenie
    const doCreate = async () => {
        const name = newTag.trim();
        if (!name) return;
        try {
            const created = await createTag(name);
            setItems(prev => [...prev, created]);
            setNewTag('');
        } catch (e: any) {
            alert(e?.error || 'Błąd przy dodawaniu taga');
        }
    };

    // edycja
    const startEdit = (t: TagRow) => {
        setEditingId(t.id);
        setEditValue(t.name);
        setTimeout(() => {
            editInputRefs.current[t.id]?.focus();
            editInputRefs.current[t.id]?.select();
        }, 0);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditValue('');
    };

    const saveEdit = async (id: number) => {
        const val = editValue.trim();
        if (!val) return;
        setSavingId(id);
        try {
            await updateTag(id, val);
            setItems(prev => prev.map(t => (t.id === id ? { ...t, name: val } : t)));
            setEditingId(null);
            setEditValue('');
        } catch (e: any) {
            alert(e?.error || 'Błąd przy zapisie');
        } finally {
            setSavingId(null);
        }
    };

    // usuwanie
    const remove = async (id: number) => {
        if (!window.confirm('Usunąć ten tag?')) return;
        try {
            await deleteTag(id);
            setItems(prev => prev.filter(t => t.id !== id));
            setOpenSet(prev => { const ns = new Set(prev); ns.delete(id); return ns; });
            setContentsByTag(prev => { const cp = { ...prev }; delete cp[id]; return cp; });
            setLoadingByTag(prev => { const cp = { ...prev }; delete cp[id]; return cp; });
        } catch (e: any) {
            alert(e?.error || 'Błąd przy usuwaniu');
        }
    };

    // rozwijanie panelu treści
    const toggleOpen = async (tag: TagRow) => {
        const id = tag.id;
        setOpenSet(prev => {
            const ns = new Set(prev);
            if (ns.has(id)) ns.delete(id); else ns.add(id);
            return ns;
        });

        if (!contentsByTag[id]) {
            setLoadingByTag(prev => ({ ...prev, [id]: true }));
            try {
                const list = await fetchContentsForTagName(tag.name);
                setContentsByTag(prev => ({ ...prev, [id]: list || [] }));
            } catch {
                setContentsByTag(prev => ({ ...prev, [id]: [] }));
            } finally {
                setLoadingByTag(prev => ({ ...prev, [id]: false }));
            }
        }
    };

    return (
        <div className="tags-screen catm-box">
            <div className="catm-header">
                <h2>Tagi</h2>
                <div className="catm-header-right">
                    <div className="catm-search">
                        <input
                            placeholder="Szukaj po ID / nazwie…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    const q = search.trim();
                                    if (q.length >= 2) {
                                        searchTagsApi(q).then(res => {
                                            setItems(res as any); // endpoint zwraca [{id,name}] albo TagWithCount – fallback niżej
                                            setOpenSet(new Set());
                                            setContentsByTag({});
                                        }).catch(() => {/* zostaw lokalny filter */ });
                                    }
                                }
                            }}
                        />
                    </div>
                    <div className="tags-new-inline">
                        <input
                            placeholder="Nowy tag"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && doCreate()}
                        />
                        <button className="btn-primary" onClick={doCreate}>Dodaj tag</button>
                        <button className="btn-ghost" onClick={load}>Odśwież</button>
                    </div>
                </div>
            </div>

            {error && <div className="catm-error">{error}</div>}
            {loading && <div className="catm-loading">Ładowanie…</div>}

            <ul className="catm-accordion">
                {filtered.map((t) => {
                    const open = openSet.has(t.id);
                    const list = contentsByTag[t.id] || [];
                    const isLoadingList = !!loadingByTag[t.id];
                    const isEditing = editingId === t.id;

                    return (
                        <li key={t.id} className={`catm-row ${open ? 'open' : ''}`}>
                            <div className="catm-row-main no-thumb">
                                {/* brak miniatury – układ 1fr | auto */}
                                <div className="catm-row-meta">
                                    {!isEditing ? (
                                        <>
                                            <div className="catm-row-title">
                                                <span className="catm-id">#{t.id}</span>
                                                <span className="catm-name">{t.name}</span>
                                            </div>
                                            <div className="catm-row-sub">
                                                Treści z tym tagiem: <span className="count-badge">{t.content_count ?? '—'}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="catm-edit-inline">
                                            <input
                                                //ref={(el) => (editInputRefs.current[t.id] = el)}
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                placeholder="Nazwa tagu"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveEdit(t.id);
                                                    if (e.key === 'Escape') cancelEdit();
                                                }}
                                            />
                                            <div className="catm-edit-inline-actions">
                                                <button className="btn" onClick={() => saveEdit(t.id)} disabled={savingId === t.id || !editValue.trim()}>
                                                    {savingId === t.id ? 'Zapisywanie…' : 'Zapisz'}
                                                </button>
                                                <button className="btn ghost" onClick={cancelEdit}>Anuluj</button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="catm-row-actions">
                                    {/* rozwiń/zwiń contents */}
                                    <button
                                        className="btn small ghost"
                                        onClick={() => toggleOpen(t)}
                                        aria-expanded={open}
                                        aria-controls={`tag-panel-${t.id}`}
                                        title={open ? 'Zwiń treści' : 'Pokaż treści'}
                                    >
                                        {open ? 'Zwiń treści' : 'Pokaż treści'}
                                    </button>

                                    {/* edycja */}
                                    {!isEditing && (
                                        <button
                                            className="icon-btn edit"
                                            onClick={() => startEdit(t)}
                                            aria-label="Edytuj"
                                            title="Edytuj nazwę"
                                        >
                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M12 20h9"></path>
                                                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
                                            </svg>
                                        </button>
                                    )}

                                    {/* Usuń */}
                                    <button
                                        className="icon-btn delete"
                                        onClick={() => remove(t.id)}
                                        aria-label="Usuń"
                                        title="Usuń tag"
                                    >
                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M3 6h18"></path>
                                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* panel treści */}
                            <div id={`tag-panel-${t.id}`} className="catm-row-panel" style={{ display: open ? 'block' : 'none' }}>
                                {isLoadingList ? (
                                    <div className="catm-loading">Ładowanie treści…</div>
                                ) : (
                                    <div className="catm-table-wrap">
                                        <table className="catm-table">
                                            <thead>
                                                <tr>
                                                    <th>ID</th><th>Tytuł</th><th>Typ</th><th>Widoczność</th><th>Premium</th><th>Data</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {list.map((ci) => (
                                                    <tr key={ci.id}>
                                                        <td>{ci.id}</td>
                                                        <td>{ci.title}</td>
                                                        <td>{ci.type}</td>
                                                        <td className="strong">
                                                            <span className={`badge ${ci.is_visible ? 'badge-success' : 'badge-muted'}`}>{ci.is_visible ? 'TAK' : 'NIE'}</span>
                                                        </td>
                                                        <td className="strong">
                                                            <span className={`badge ${ci.is_premium ? 'badge-success' : 'badge-muted'}`}>{ci.is_premium ? 'TAK' : 'NIE'}</span>
                                                        </td>
                                                        <td>{new Date(ci.created_at).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                                {!list.length && (
                                                    <tr>
                                                        <td colSpan={6} style={{ textAlign: 'center', opacity: 0.7 }}>Brak treści z tym tagiem</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
                {!filtered.length && <li className="empty">Brak tagów</li>}
            </ul>
        </div>
    );
}
